import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCrmServices,
  createCrmService,
  updateCrmService,
  deleteCrmService,
} from "../features/crm/crmThunks.js";
import { Pencil, Trash2 } from "lucide-react";
import "../styles/crm/crmservices.css";

const CrmServices = () => {
  const dispatch = useDispatch();

  const crmState = useSelector((state) => state.crm || {});
  const services = crmState.services || [];
  const loading = crmState.loading;
  const error = crmState.error;

  const [onlyActiveFilter, setOnlyActiveFilter] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [editingServiceId, setEditingServiceId] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    defaultPrice: "",
    estimatedDurationMinutes: "",
    isActive: true,
  });

  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal eliminar (confirm)
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  useEffect(() => {
    dispatch(fetchCrmServices({ onlyActive: true }));
  }, [dispatch]);

  // UX: cerrar modal con ESC + bloquear scroll mientras esté abierto el modal
  useEffect(() => {
    if (!deleteOpen) return;

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (deleting) return;

      setDeleteOpen(false);
      setServiceToDelete(null);
      setDeleteError(null);
    };

    document.addEventListener("keydown", handleKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [deleteOpen, deleting]);

  const fetchWithActiveFilter = (onlyActive) => {
    setOnlyActiveFilter(onlyActive);
    dispatch(fetchCrmServices({ onlyActive }));
  };

  const filteredServices = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return services;

    return services.filter((service) => {
      const name = String(service.name || "").toLowerCase();
      const category = String(service.category || "").toLowerCase();
      return name.includes(query) || category.includes(query);
    });
  }, [services, searchQuery]);

  const resetForm = () => {
    setFormData({
      name: "",
      category: "",
      defaultPrice: "",
      estimatedDurationMinutes: "",
      isActive: true,
    });
    setFormError(null);
    setIsSubmitting(false);
    setEditingServiceId(null);
    setFormMode("create");
  };

  const handleOpenCreateForm = () => {
    resetForm();
    setShowForm(true);
  };

  const handleOpenEditForm = (service) => {
    setFormData({
      name: service.name || "",
      category: service.category || "",
      defaultPrice:
        service.default_price !== null && service.default_price !== undefined
          ? String(service.default_price)
          : "",
      estimatedDurationMinutes:
        service.estimated_duration_minutes !== null &&
        service.estimated_duration_minutes !== undefined
          ? String(service.estimated_duration_minutes)
          : "",
      isActive: service.is_active !== undefined ? service.is_active : true,
    });

    setFormError(null);
    setIsSubmitting(false);
    setEditingServiceId(service.id);
    setFormMode("edit");
    setShowForm(true);
  };

  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setFormError(null);

    const serviceName = formData.name.trim();
    if (!serviceName) {
      setFormError("El nombre del servicio es obligatorio.");
      return;
    }

    const payload = {
      name: serviceName,
      category: formData.category || "",
      default_price: formData.defaultPrice !== "" ? Number(formData.defaultPrice) : null,
      estimated_duration_minutes:
        formData.estimatedDurationMinutes !== "" ? Number(formData.estimatedDurationMinutes) : null,
      is_active: !!formData.isActive,
    };

    setIsSubmitting(true);

    if (formMode === "create") {
      const resultAction = await dispatch(createCrmService(payload));

      if (createCrmService.fulfilled.match(resultAction)) {
        resetForm();
        setShowForm(false);
      } else {
        const err = resultAction.payload || { detail: "No se pudo crear el servicio." };
        setFormError(err.detail || "No se pudo crear el servicio.");
      }
    }

    if (formMode === "edit" && editingServiceId) {
      const resultAction = await dispatch(
        updateCrmService({
          serviceId: editingServiceId,
          serviceData: payload,
        })
      );

      if (updateCrmService.fulfilled.match(resultAction)) {
        resetForm();
        setShowForm(false);
      } else {
        const err = resultAction.payload || { detail: "No se pudo actualizar el servicio." };
        setFormError(err.detail || "No se pudo actualizar el servicio.");
      }
    }

    setIsSubmitting(false);
  };

  // ---------- DELETE MODAL ----------
  const openDeleteModal = (service) => {
    setDeleteError(null);
    setServiceToDelete(service);
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setServiceToDelete(null);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!serviceToDelete?.id) return;

    setDeleteError(null);
    setDeleting(true);

    const resultAction = await dispatch(deleteCrmService(serviceToDelete.id));

    setDeleting(false);

    if (deleteCrmService.fulfilled.match(resultAction)) {
      closeDeleteModal();
      return;
    }

    const err = resultAction.payload || { detail: "No se pudo eliminar el servicio." };
    setDeleteError(err.detail || "No se pudo eliminar el servicio.");
  };

  if (loading && services.length === 0) return <div>Cargando servicios...</div>;

  if (error) {
    return <div>Error al cargar servicios: {error.detail || "Error"}</div>;
  }

  return (
    <div className="crm-services-container">
      <div className="crm-services-header">
        <div>
          <h2>Servicios</h2>
          <p className="crm-services-subtitle">
            Catálogo de cirugías y tratamientos de la clínica.
          </p>
        </div>

        <div className="crm-services-actions">
          <form className="crm-services-search-form" onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              placeholder="Buscar por nombre o categoría…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="btn-primary btn-xs">
              Buscar
            </button>
          </form>

          <div className="crm-services-filters segmented">
            <button
              type="button"
              className={`segmented-btn ${onlyActiveFilter ? "is-active" : ""}`}
              onClick={() => fetchWithActiveFilter(true)}
            >
              Solo activos
            </button>
            <button
              type="button"
              className={`segmented-btn ${!onlyActiveFilter ? "is-active" : ""}`}
              onClick={() => fetchWithActiveFilter(false)}
            >
              Todos
            </button>
          </div>

          <button type="button" className="btn-primary btn-sm" onClick={handleOpenCreateForm}>
            Nuevo servicio
          </button>

          {loading ? <div className="crm-services-inline-status">Actualizando…</div> : null}
        </div>
      </div>

      {showForm && (
        <div className="crm-service-form-wrapper">
          <form className="crm-service-form" onSubmit={handleFormSubmit}>
            <h3>{formMode === "create" ? "Nuevo servicio" : "Editar servicio"}</h3>

            <div className="crm-service-form-row">
              <div className="crm-service-field">
                <label htmlFor="name">Nombre</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Ej: Aumento de pecho"
                />
              </div>

              <div className="crm-service-field">
                <label htmlFor="category">Categoría</label>
                <input
                  id="category"
                  name="category"
                  type="text"
                  value={formData.category}
                  onChange={handleFormChange}
                  placeholder="Ej: Cirugía mamaria, Corporal..."
                />
              </div>

              <div className="crm-service-field">
                <label htmlFor="defaultPrice">Precio base (€)</label>
                <input
                  id="defaultPrice"
                  name="defaultPrice"
                  type="number"
                  step="0.01"
                  value={formData.defaultPrice}
                  onChange={handleFormChange}
                  placeholder="Ej: 4500"
                />
              </div>

              <div className="crm-service-field">
                <label htmlFor="estimatedDurationMinutes">Duración estimada (min)</label>
                <input
                  id="estimatedDurationMinutes"
                  name="estimatedDurationMinutes"
                  type="number"
                  value={formData.estimatedDurationMinutes}
                  onChange={handleFormChange}
                  placeholder="Ej: 120"
                />
              </div>

              <div className="crm-service-field crm-service-field-inline">
                <label htmlFor="isActive">Activo</label>
                <input
                  id="isActive"
                  name="isActive"
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={handleFormChange}
                />
              </div>
            </div>

            {formError ? <div className="crm-service-form-error">{formError}</div> : null}

            <div className="crm-service-form-actions">
              <button type="submit" className="btn-primary btn-sm" disabled={isSubmitting}>
                {isSubmitting
                  ? "Guardando..."
                  : formMode === "create"
                  ? "Crear servicio"
                  : "Guardar cambios"}
              </button>

              <button
                type="button"
                className="ghost btn-sm"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {filteredServices.length === 0 ? (
        <p>No hay servicios registrados.</p>
      ) : (
        <table className="crm-table">
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Categoría</th>
              <th>Precio base</th>
              <th>Duración estimada</th>
              <th>Activo</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {filteredServices.map((service) => {
              const hasPrice =
                service.default_price !== null && service.default_price !== undefined;
              const hasDuration =
                service.estimated_duration_minutes !== null &&
                service.estimated_duration_minutes !== undefined;

              return (
                <tr key={service.id}>
                  <td>{service.name}</td>
                  <td>{service.category || "-"}</td>
                  <td>{hasPrice ? `${service.default_price} €` : "-"}</td>
                  <td>{hasDuration ? `${service.estimated_duration_minutes} min` : "-"}</td>
                  <td>{service.is_active ? "Sí" : "No"}</td>
                  <td className="crm-services-table-actions">
                    <div className="crm-services-action-icons">
                      <button
                        type="button"
                        className="crm-icon-btn"
                        onClick={() => handleOpenEditForm(service)}
                        title="Editar"
                        aria-label="Editar servicio"
                      >
                        <Pencil size={18} />
                      </button>

                      <button
                        type="button"
                        className="crm-icon-btn danger"
                        onClick={() => openDeleteModal(service)}
                        title="Eliminar"
                        aria-label="Eliminar servicio"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* MODAL ELIMINAR */}
      {deleteOpen && (
        <div className="ui-modal-overlay" onClick={closeDeleteModal} role="presentation">
          <div
            className="ui-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Eliminar servicio"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ui-modal-header">
              <h3>Eliminar servicio</h3>
            </div>

            <div className="ui-modal-body ui-modal-body--single">
              <div className="ui-delete-help">
                ¿Seguro que quieres eliminar{" "}
                <strong>{serviceToDelete?.name || `Servicio #${serviceToDelete?.id}`}</strong>?
                <div className="ui-delete-subhelp">Esta acción no se puede deshacer.</div>
              </div>

              {deleteError ? <div className="ui-modal-error">{deleteError}</div> : null}
            </div>

            <div className="ui-modal-footer">
              <button
                type="button"
                className="ghost btn-sm"
                onClick={closeDeleteModal}
                disabled={deleting}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="btn-danger btn-sm"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? "Eliminando…" : "Eliminar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrmServices;